import React, { JSX } from "react";
import styles from "../assets/style.module.css";
import * as ReactRouterDom from "react-router-dom";
import { ApiResponseData, BaseService, IMember, ITeamMember, ITeamWebsite, OmneHostService, SanitizedResponse } from "../../../../services";
import { Classname, Paginator, Scheme, useSearchParams } from "@rivtn/noseur";
import { Utils } from "../../../../utils";

interface MemberCardProps {
    member: ITeamMember;
    onSelect: (m: ITeamMember) => void;
}

export function MemberCard({ member: m, onSelect }: MemberCardProps): JSX.Element {
    const roleClass: string = m.role === "MODERATOR"
        ? styles["role-manager"]
        : m.role === "OWNER"
            ? styles["role-coach"]
            : m.status === "PAST"
                ? styles["role-past"]
                : styles["role-member"];

    const roleLabel: string = m.role === "MODERATOR" ? "Moderator" : m.role === "OWNER" ? "Owner" : m.status === "PAST" ? "Former" : "Member";

    return (
        <div
            className={`${styles["member-card"]} ${m.role === "MODERATOR" ? styles["MODERATOR"] : ""}`}
            onClick={() => onSelect(m)}
        >
            {m.role === "MODERATOR" && <div className={styles["member-manager-crown"]}><i className="fa fa-crown" /></div>}
            <img className={styles["member-img"]} src={m.passport_picture_url} alt={m.display_name} loading="lazy" />
            <div className={styles["member-body"]}>
                <span className={`${styles["member-role-badge"]} ${roleClass}`}>{roleLabel}</span>
                <div className={styles["member-name"]}>{m.display_name}</div>
                {(!!m.settings.country) && (<div className={styles["member-country"]}>{m.settings.country}</div>)}
            </div>
        </div>
    );
}

interface MemberModalProps {
    member: ITeamMember;
    onClose: () => void;
}

export function MemberModal({ member: m, onClose }: MemberModalProps): JSX.Element {
    const roleClass: string = m.role === "MODERATOR"
        ? styles["role-manager"]
        : m.role === "OWNER"
            ? styles["role-coach"]
            : m.status === "past"
                ? styles["role-past"]
                : styles["role-member"];

    const roleLabel: string = m.role === "MODERATOR" ? "Team Moderator" : m.role === "OWNER" ? "Owner" : "Member";

    return (
        <div
            className={styles["modal-overlay"]}
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <div className={styles["modal"]}>
                <button className={styles["modal-close"]} onClick={onClose}><i className="fa fa-xmark" /></button>

                <div className={styles["modal-banner"]}>
                    <img style={{ width: "100%", height: "100%", objectFit: "cover", opacity: 0.3 }} src={m.half_body_picture_url} alt="" />
                    <div className={styles["modal-banner-overlay"]} />
                    <img className={styles["modal-avatar"]} src={m.passport_picture_url} alt={m.display_name} />
                </div>

                <div className={styles["modal-body"]}>
                    <div className={styles["modal-name-row"]}>
                        <div className={styles["modal-name"]}>{m.display_name}</div>
                        <span className={`${styles["member-role-badge"]} ${roleClass}`} style={{ marginBottom: 0 }}>{roleLabel}</span>
                        {m.status === "ACTIVE"
                            ? <span className={`${styles["status-indicator"]} ${styles["status-active"]}`}><i className="fa fa-circle" style={{ fontSize: "0.55em", verticalAlign: "middle" }} /> Active</span>
                            : <span className={`${styles["status-indicator"]} ${styles["status-inactive"]}`}><i className="fa fa-circle" style={{ fontSize: "0.55em", verticalAlign: "middle" }} /> Former</span>
                        }
                    </div>

                    {(!!m.profile_details) && (<div style={{ color: "var(--muted)", fontSize: "0.85rem", margin: "1rem 0px" }}>"{m.profile_details}"</div>)}

                    <div className={styles["modal-info-grid"]}>
                        {(m.extra_data.team_role) && (<div className={styles["modal-info-card"]}>
                            <div className={styles["modal-info-lbl"]}>Role</div>
                            <div className={styles["modal-info-val"]}>{m.extra_data.team_role}</div>
                        </div>)}
                        {(m.settings.country) && (<div className={styles["modal-info-card"]}>
                            <div className={styles["modal-info-lbl"]}>Country</div>
                            <div className={styles["modal-info-val"]}>{m.settings.country}</div>
                        </div>)}
                        {(m.settings.state_region) && (<div className={styles["modal-info-card"]}>
                            <div className={styles["modal-info-lbl"]}>State / Region</div>
                            <div className={styles["modal-info-val"]}>{m.settings.state_region}</div>
                        </div>)}
                        {(m.join_date) && (<div className={styles["modal-info-card"]}>
                            <div className={styles["modal-info-lbl"]}>Date Joined</div>
                            <div className={styles["modal-info-val"]}>{Utils.fineDate(new Date(m.join_date))}</div>
                        </div>)}
                        {(m.exit_date) && (<div className={styles["modal-info-card"]}>
                            <div className={styles["modal-info-lbl"]}>Date Left</div>
                            <div className={styles["modal-info-val"]}>{Utils.fineDate(new Date(m.exit_date))}</div>
                        </div>)}
                    </div>

                    {Object.keys(m.social_accounts).length > 0 && (
                        <>
                            <div className={styles["modal-ach-title"]}>Social Networks</div>
                            <div className={styles["modal-social-row"]}>
                                {Object.entries(m.social_accounts).map(([social, username]) => (<ReactRouterDom.NavLink target="_blank" key={social} to={Utils.getSocialLink(social, username)} className={styles["social-link-btn"]} style={{ textDecoration: "none" }}>
                                    <i className={Classname.build(styles["icon"], Utils.getSocialIcon(social))} /> {username}
                                </ReactRouterDom.NavLink>))}
                            </div>
                        </>
                    )}

                    {m.extra_data?.recent_personal_achievements?.length > 0 && (
                        <div className={styles["modal-achievements"]}>
                            <div className={styles["modal-ach-title"]}>Achievements</div>
                            <div className={styles["modal-ach-list"]}>
                                {m.extra_data?.recent_personal_achievements.map((a: string) => (
                                    <span key={a} className={styles["modal-ach-tag"]}>{a}</span>
                                ))}
                            </div>
                        </div>
                    )}

                    {(m.settings.show_link_to_profile) && (<div style={{ marginTop: "2.5rem" }}>
                        <ReactRouterDom.NavLink to={`${window.location.origin}/me/${m.member_id}`} target="_blank" className={styles["btn-ghost"]} style={{ fontSize: "0.82rem", padding: "0.5rem 1.2rem", textDecoration: "none" }}>
                            View Full Profile <i className="fa fa-arrow-right" />
                        </ReactRouterDom.NavLink>
                    </div>)}
                </div>
            </div>
        </div>
    );
}

interface MembersProps {
    website: ITeamWebsite;
}

function Members(props: MembersProps): JSX.Element {

    const omnehostService = OmneHostService.getInstance();
    const [members, setMembers] = React.useState<ApiResponseData<ITeamMember>>();
    const [selectedMember, setSelectedMember] = React.useState<ITeamMember | null>(null);
    const { page, setPage, status, setStatus } = useSearchParams({ defaultInit: window.location.search, params: { page: "1", status: "ACTIVE" } });

    React.useEffect(fetchInformation, [omnehostService, page, status]);

    return (<>
        <div className={styles["section"]}>
            <div className={styles["sec-line"]} />
            <h1 className={styles["sec-title"]}>Team <em>Squad</em></h1>
            <p className={styles["sec-sub"]}>Click any member to view their full profile</p>

            <div className={styles["tabs"]}>
                {([["ACTIVE", "Current Squad"], ["LEFT", "Former Members"], ["", "All Members"]] as [string, string][]).map(([v, l]) => (
                    <button
                        key={v}
                        className={`${styles["tab-btn"]} ${status === v ? styles["active"] : ""}`}
                        onClick={() => setStatus(v)}
                    >
                        {l}
                    </button>
                ))}
            </div>

            {(!!members?.content)
                ? ((members.content && members.content?.length > 0)
                    ? (<div className={styles["members-grid"]}>
                        {members.content!.map((m) => (<MemberCard key={m.external_id} member={m} onSelect={setSelectedMember} />))}
                    </div>)
                    : (Utils.emptyState()))
                : (Utils.loadingState())}
            <Paginator scheme={Scheme.LIGHT} style={{ marginTop: 50 }} rowsPerPage={members?.size} totalRecords={members?.total_elements} initialPage={parseInt("" + page)} onPageChange={(e) => {
                setPage("" + e.currentPage)
            }} template={{
                activePageLabel: () => "",
                nextPageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, "fa fa-angle-right")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
                previousPageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, "fa fa-angle-left")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
                lastPageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, "fa fa-angle-double-right")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
                firstPageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, "fa fa-angle-double-left")} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick} />),
                pageElement: (option) => (<button className={Classname.build(`${styles["page-btn"]}`, option.className, (option.active ? styles["active"] : null))} style={{ margin: "0px 2.5px" }} disabled={option.disabled} onClick={option.onClick}>{option.label}</button>),
            }} />
        </div>

        {selectedMember && <MemberModal member={selectedMember} onClose={() => setSelectedMember(null)} />}
    </>);

    function fetchInformation() {
        setMembers({});
        omnehostService.queryTeamMembers(props.website.team.public_id, {
            page,
            status,
            sort: "role,DESC",
        }).then(({ apiResponse }: SanitizedResponse<ITeamMember>) => {
            setMembers(apiResponse.data!);
        }).catch(BaseService.reportError);
    }

}

export default Members;